import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { SOBRE_MI } from '../constants/testIds'
import { useSiteContent } from '../contexts/SiteContentContext'

const BF = 'Anton, sans-serif'
const MONO = 'JetBrains Mono, monospace'
const SG = 'Space Grotesk, sans-serif'

function RevealLine({ children, delay = 0, className = '' }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <div ref={ref} style={{ overflow: 'hidden', paddingTop: '0.1em', marginTop: '-0.1em', paddingBottom: '0.06em', marginBottom: '-0.06em' }} className={className}>
      <motion.div
        initial={{ y: '105%' }}
        animate={inView ? { y: 0 } : {}}
        transition={{ delay, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  )
}

function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  )
}

export default function SobreMi() {
  const { content } = useSiteContent()
  const sm = content.sobre_mi
  const headings = sm.headings || ['DETRÁS DE', 'CADA WEB,', 'ESTOY YO.']
  const manifesto = sm.manifesto || []
  const photo2 = content.hero?.photo2_url

  return (
    <section id="sobre-mi" data-testid={SOBRE_MI.section} className="relative bg-black py-24 md:py-36">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 xl:px-16">

        <FadeIn delay={0}>
          <div className="flex items-center gap-4 mb-14">
            <span className="accent-rule" />
            <span className="text-xs tracking-[0.35em] text-amber-400 uppercase" style={{ fontFamily: MONO }}>Sobre mí</span>
          </div>
        </FadeIn>

        {/* Two-column: heading + photo */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16 items-center">
          <div>
            <RevealLine delay={0.1}>
              <h2 className="uppercase text-white leading-none"
                style={{ fontFamily: BF, fontSize: 'clamp(48px, 7vw, 110px)', letterSpacing: '0.01em' }}>
                {headings[0]}
              </h2>
            </RevealLine>
            <RevealLine delay={0.22}>
              <h2 className="uppercase leading-none"
                style={{ fontFamily: BF, fontSize: 'clamp(48px, 7vw, 110px)', letterSpacing: '0.01em', color: '#F5A623' }}>
                {headings[1]}
              </h2>
            </RevealLine>
            <RevealLine delay={0.34}>
              <h2 className="uppercase text-white leading-none"
                style={{ fontFamily: BF, fontSize: 'clamp(48px, 7vw, 110px)', letterSpacing: '0.01em' }}>
                {headings[2]}
              </h2>
            </RevealLine>
          </div>

          {/* Photo 2 — visible portrait */}
          {photo2 && (
            <FadeIn delay={0.3}>
              <div className="relative overflow-hidden" style={{ aspectRatio: '3/4', maxHeight: '480px' }}>
                <img
                  src={photo2}
                  alt="José Osuna"
                  className="w-full h-full object-cover object-center"
                  style={{ filter: 'brightness(0.88) contrast(1.05)' }}
                />
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.7) 100%)'
                }} />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-white/40 text-xs tracking-[0.3em] uppercase" style={{ fontFamily: MONO }}>
                    José Osuna · Córdoba
                  </span>
                </div>
              </div>
            </FadeIn>
          )}
        </div>

        {/* Bio two-col */}
        <FadeIn delay={0.5}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-20 mb-12 max-w-4xl">
            <p data-testid={SOBRE_MI.bio} className="text-white/60 leading-relaxed" style={{ fontFamily: SG, fontSize: 'clamp(15px, 1.1vw, 18px)' }}>
              {sm.bio1}
            </p>
            <p className="text-white/60 leading-relaxed" style={{ fontFamily: SG, fontSize: 'clamp(15px, 1.1vw, 18px)' }}>
              {sm.bio2}
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.6} className="mb-24">
          <a href="#contacto" data-testid={SOBRE_MI.cta}
            onClick={(e) => { e.preventDefault(); document.querySelector('#contacto')?.scrollIntoView({ behavior: 'smooth' }) }}
            className="inline-flex items-center gap-3 text-amber-400 hover:text-white transition-colors duration-300 group"
            style={{ fontFamily: MONO, fontSize: '13px', letterSpacing: '0.2em' }}>
            <span className="uppercase">Hablamos de tu proyecto</span>
            <span className="text-xl transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1">↗</span>
          </a>
        </FadeIn>

        {/* Manifesto */}
        <div className="border-t border-neutral-900 pt-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
            {manifesto.map((item, i) => (
              <FadeIn key={item.num} delay={0.1 * i}>
                <div className="border-l border-neutral-800 pl-7 py-6 pr-8">
                  <div className="text-amber-400 mb-5" style={{ fontFamily: MONO, fontSize: '13px', letterSpacing: '0.2em' }}>{item.num}</div>
                  <h3 className="text-white uppercase mb-4"
                    style={{ fontFamily: BF, fontSize: 'clamp(17px, 1.5vw, 22px)', letterSpacing: '0.01em', lineHeight: 1.1 }}>
                    {item.title}
                  </h3>
                  <p className="text-white/45 leading-relaxed" style={{ fontFamily: SG, fontSize: 'clamp(13px, 1vw, 15px)' }}>
                    {item.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

      </div>
    </section>
  )
}
